import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { AppState } from 'src/AppState';
import { EasyClaimDocumentType } from 'src/easyclaim.api.client.typescript/src';
import { HintButton } from 'src/easyclaim.app.components/common/HintButton';
import { VinPlaceholder } from 'src/easyclaim.app.components/common/ImageRepository';
import ShowExamplePhoto from 'src/easyclaim.app.components/common/ShowPhotoExample';
import { VinSlider } from 'src/easyclaim.app.components/modals/VinSlider';
import { CustomerConfiguration } from 'src/easyclaim.app.contexts/CustomerConfigurationContextCore';
import { reportError } from 'src/easyclaim.app.libs/reportError';

import styles from '../../easyclaim.app.components/common/styles/HintButton.module.css';
import {
	ExampleModalImage,
	SelectImage,
	StyledInfoIcon,
} from '../../easyclaim.app.components/index';
import { Image } from '../../easyclaim.app.services/ImageSelectionService';
import { FourAdditionalPicturesProps, isAnyOfImagesLoading } from '../FourAdditionalPictures';

export function VinControl(currentAppState: FourAdditionalPicturesProps) {
	const [isSliderOpen, setIsSliderOpen] = useState(false);
	const config = CustomerConfiguration();

	return (
		<>
			<h3 className="four-additional-pictures-vin-title">
				<FormattedMessage id="fourAdditionalPictures.vin.title" />
			</h3>
			<p className="four-additional-pictures-vin-description">
				<FormattedMessage id="fourAdditionalPictures.vin.description" />
				<StyledInfoIcon
					onClick={() => {
						showVinExampleImage(currentAppState).catch(reportError);
					}}
				/>
			</p>
			<ShowExamplePhoto
				id="four-diagonal-perspectives-vehicle-front-left-description"
				onClick={() => {
					showVinExampleImage(currentAppState).catch(reportError);
				}}
			>
				{(description) => (
					<p className="four-diagonal-perspectives-vehicle-front-left-description">{description}</p>
				)}
			</ShowExamplePhoto>
			{config?.showUpdatedVerificationResults && (
				<div className={styles.hintButtonContainer}>
					<HintButton onClick={() => setIsSliderOpen(true)} />
				</div>
			)}
			<div className="four-additional-pictures-detail-container">
				<SelectImage
					componentId="vinPicture"
					fileType={EasyClaimDocumentType.VinPicture}
					caseId={currentAppState.easyClaimCase?.caseId}
					easyClaimService={currentAppState.easyClaimService}
					currentImage={currentAppState.vinImage}
					title="vinPicture.headline"
					marginBottom="1rem"
					onChange={(image) => {
						const newState = vinImageChanged(currentAppState, image);

						void currentAppState.setAppState(newState);
					}}
					onShowExample={() => {
						showVinExampleImage(currentAppState).catch(reportError);
					}}
					showUserFeedbackInfo={true}
					placeholder={<VinPlaceholder />}
					configuration={currentAppState.easyClaimCase!._configuration!}
					inputError={currentAppState.inputError}
				/>
			</div>
			<VinSlider isOpen={isSliderOpen} onClose={() => setIsSliderOpen(false)} />
		</>
	);
}

function vinImageChanged(currentAppState: AppState, image: Image | undefined): AppState {
	// const isImageLoading = ImageSelectionService.isImageLoading(image);
	let images: Image[] = [];

	images = currentAppState.overallDashboardImage
		? images.concat([currentAppState.overallDashboardImage])
		: images;
	images = currentAppState.dashboardImage
		? images.concat([currentAppState.dashboardImage])
		: images;
	images = currentAppState.windshieldImage
		? images.concat([currentAppState.windshieldImage])
		: images;
	images = image ? images.concat([image]) : images;

	const isImageLoading = isAnyOfImagesLoading(images);

	const newState = Object.assign({}, currentAppState, {
		vinImage: image,
		isImageLoading: isImageLoading,
	});

	return newState;
}

function showVinExampleImage(currentAppState: AppState): Promise<void> {
	const shouldUseWebp =
		currentAppState.easyClaimCase?._configuration?.canUseExperimentalPerformanceEnhancer === true;
	const imageSrc = shouldUseWebp
		? `${process.env.PUBLIC_URL}/images/WEBP/example-vin-windshield-2.webp`
		: `${process.env.PUBLIC_URL}/images/example-vin-windshield-2.jpg`;
	const images: ExampleModalImage[] = [{ src: imageSrc }];

	return currentAppState.showExampleModal(
		images,
		'fourAdditionalPictures.vin.exampleModalTitle',
		'fourAdditionalPicturesExampleModal.button.close',
		'vin'
	);
}
